import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, BrainCircuit, CheckCircle, ShieldCheck, Users, Compass, Sparkles } from 'lucide-react';

const AboutPage = () => {
    return (
        <div className="bg-[#f5fefd] min-h-screen font-sans text-slate-600">

            {/* 2.1. Hero Bölümü */}
            <section className="py-20 px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Say-Hot nedir?</h1>
                    <h2 className="text-xl md:text-2xl font-semibold text-blue-600 mb-6">
                        81 il için etkinlik ve gezi rehberini, kişiselleştirilmiş yapay zekâ planlayıcıyla birleştiren seyahat asistanı.
                    </h2>
                    <p className="text-base md:text-lg leading-relaxed mb-8 text-slate-700">
                        Say-Hot, Türkiye’nin dört bir yanındaki etkinlikleri, doğa rotalarını ve kapalı mekân duyurularını tek ekranda toplayan;
                        bunları kullanarak sana özel seyahat planı üreten yapay zekâ destekli bir gezi rehberidir.
                        Amacımız, “Bu hafta nereye gitsek?” sorusunu tek tıkla cevaplayan, güvenilir ve pratik bir yol arkadaşı olmak.
                    </p>

                    <div className="inline-block">
                        <Link
                            to="/start-plan"
                            className="inline-block bg-blue-600 text-white font-bold text-lg px-8 py-3 rounded-full shadow-lg hover:bg-blue-700 transition-all transform hover:scale-105"
                        >
                            Bu hafta bana plan öner
                        </Link>
                        <p className="mt-3 text-sm text-slate-500">Şehrini ve tarihini seç, gerisini yapay zekâ halletsin.</p>
                    </div>
                </div>
            </section>

            {/* 2.2. Misyonumuz */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-[0_18px_40px_rgba(15,23,42,0.08)] p-8 md:p-12 text-center border border-slate-100">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Misyonumuz</h3>
                    <p className="text-base md:text-lg leading-relaxed text-slate-700">
                        Say-Hot, Türkiye’de gezi planlamayı zahmetli bir araştırma süreci olmaktan çıkarıp, birkaç basit soruyla çözülebilen akıllı bir deneyime dönüştürmek için kuruldu.
                        Her şehir için ayrı ayrı onlarca site gezmek, sosyal medyada kaybolmak, etkinlik tarihlerini tek tek takip etmek yerine;
                        tüm bu bilgileri senin adına toplayan, anlamlandıran ve sana en uygun rotayı öneren bir sistem tasarlıyoruz.
                        Hedefimiz; hafta sonu kaçamağından uzun tatillere kadar her yolculuğu, kişiselleştirilmiş yapay zekâ desteğiyle daha planlı, daha keyifli ve daha ulaşılabilir kılmak.
                    </p>
                </div>
            </section>

            {/* 2.3. Nasıl Çalışır? */}
            <section className="py-16 px-4 max-w-5xl mx-auto">
                <h3 className="text-2xl font-bold text-center text-gray-900 mb-12">Nasıl Çalışır?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Kart 1 */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
                            <MapPin className="w-8 h-8" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">1. Şehrini ve tarihini seç</h4>
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Gitmek istediğin ili, ilçe veya bölgeyi ve planladığın tarih aralığını seçiyorsun.
                            Sistem, bu süre içinde gerçekleşen konserleri, festivalleri, doğa aktivitelerini ve kapalı mekân duyurularını otomatik olarak tarıyor.
                        </p>
                    </div>

                    {/* Kart 2 */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-purple-600">
                            <Compass className="w-8 h-8" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">2. İlgi alanlarını işaretle</h4>
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Müzik türün, sevdiğin etkinlik tipleri, doğa yürüyüşü mü yoksa şehir içi keşif mi tercih ettiğin gibi bilgileri paylaşıyorsun.
                            Yapay zekâ, bu tercihleri kullanarak sana uygun etkinlik ve gezi noktalarını önceliklendiriyor.
                        </p>
                    </div>

                    {/* Kart 3 */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                            <BrainCircuit className="w-8 h-8" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">3. Yapay zekâdan kişisel rota al</h4>
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Say-Hot’ın yapay zekâsı; etkinlik takvimini, ulaşım sürelerini ve gün içi enerjini hesaba katarak sana özel bir gezi planı hazırlıyor.
                            Sabah–öğle–akşam akışı, mola önerileri ve alternatif seçeneklerle birlikte tek bir ekrandan tüm planı görebiliyorsun.
                        </p>
                    </div>
                </div>
            </section>

            {/* 2.4. Neleri Bulabilirsin? */}
            <section className="py-16 px-4 bg-white">
                <div className="max-w-5xl mx-auto">
                    <h3 className="text-2xl font-bold text-center text-gray-900 mb-10">Say-Hot’ta neleri bulabilirsin?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                                <span>Bu hafta şehrinde gerçekleşen konserler ve sahne etkinlikleri</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                                <span>Doğa yürüyüşü, kamp, sahil ve kaçamak rotaları</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                                <span>Müze, sergi, kültürel etkinlik ve seminerler</span>
                            </li>
                        </ul>
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                                <span>Kapalı mekân duyuruları ve bakımda olan alanlar</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                                <span>81 il için temel gezi rehberi ve ilçe önerileri</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                                <span>Blog yazılarıyla rota örnekleri ve ilham fikirleri</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* 2.5. Bizi Diğer Sitelerden Ayıran Ne? */}
            <section className="py-16 px-4 max-w-5xl mx-auto">
                <h3 className="text-2xl font-bold text-center text-gray-900 mb-12">Bizi diğer sitelerden ayıran ne?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm">
                        <h4 className="font-bold text-gray-900 mb-2">Sadece liste değil, plan üretiyoruz</h4>
                        <p className="text-sm text-slate-600">
                            Klasik etkinlik siteleri yalnızca olan biteni listelerken, Say-Hot bu bilgiyi kullanarak tam gün plan çıkarıyor.
                            Ne zaman nerede olman gerektiğini, aradaki boşlukları nasıl değerlendirebileceğini gösteriyor.
                        </p>
                    </div>
                    <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm">
                        <h4 className="font-bold text-gray-900 mb-2">Türkiye odaklı, 81 il kapsayan yaklaşım</h4>
                        <p className="text-sm text-slate-600">
                            Odak noktamız yalnızca tek bir şehir değil; Türkiye’nin tamamı.
                            Büyük şehirler kadar, az bilinen rotalar ve yerel etkinlikler de Say-Hot’ın radarında.
                        </p>
                    </div>
                    <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm">
                        <h4 className="font-bold text-gray-900 mb-2">Yapay zekâ ve editoryal denge</h4>
                        <p className="text-sm text-slate-600">
                            Tüm planlar yapay zekâ tarafından önerilse de; veri kaynaklarını seçerken insan gözüyle süzgeçten geçirilmiş,
                            güvenilir ve güncel kaynakları tercih ediyoruz.
                        </p>
                    </div>
                </div>
            </section>

            {/* 2.6. Kimler İçin Tasarlandı? */}
            <section className="py-16 px-4 bg-white">
                <div className="max-w-3xl mx-auto text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-8">Kimler için tasarlandı?</h3>
                    <div className="space-y-6 text-slate-700">
                        <div>
                            <h4 className="font-bold text-gray-900 mb-1 flex items-center justify-center gap-2">
                                <Users className="w-5 h-5 text-blue-500" /> Hafta sonu nereye gitsek diyen çiftler ve arkadaş grupları
                            </h4>
                            <p className="text-sm">Kısa zamanda, çok seçenek arasından karar vermek zorunda kalanlar için hızlı rota önerileri.</p>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900 mb-1 flex items-center justify-center gap-2">
                                <Sparkles className="w-5 h-5 text-purple-500" /> Konser, festival ve etkinlik kovalamayı sevenler
                            </h4>
                            <p className="text-sm">“Bu hafta şehirde ne var?” sorusuna tek ekranda cevap bulmak isteyenler için.</p>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900 mb-1 flex items-center justify-center gap-2">
                                <MapPin className="w-5 h-5 text-green-500" /> Yeni şehirler keşfetmek isteyen gezginler
                            </h4>
                            <p className="text-sm">Az bilinen rotaları ve yerel deneyimleri görmek isteyenler için, şehir şehir rehber yapısı.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 2.7. Veri ve Güven */}
            <section className="py-16 px-4 max-w-4xl mx-auto text-center">
                <div className="bg-blue-50 rounded-2xl p-8 border border-blue-100">
                    <ShieldCheck className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Veri ve güven yaklaşımımız</h3>
                    <p className="text-slate-700 leading-relaxed max-w-2xl mx-auto">
                        Say-Hot, kullanıcı verisini yalnızca gezi planını kişiselleştirmek için kullanır; üçüncü parti kurumlarla izinsiz paylaşmayı hedefleyen bir iş modeline sahip değildir.
                        Tercihlerin, favori etkinliklerin ve sık baktığın şehirler; sana daha iyi öneri sunmak için saklanan, dilediğinde silebileceğin basit sinyallerdir.
                        Amacımız, gezi planını kolaylaştırırken seni gereksiz bildirim ve veri karmaşasından uzak tutmak.
                    </p>
                </div>
            </section>

            {/* 2.8. Alt CTA */}
            <section className="py-20 px-4 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Hazırsan, bu hafta nereye gidelim?</h2>
                <p className="text-lg text-slate-600 mb-8">
                    Şehrini, tarihini ve ilgi alanlarını seç; Say-Hot senin için ilk kişisel seyahat planını oluştursun.
                </p>
                <Link
                    to="/start-plan"
                    className="inline-block bg-blue-600 text-white font-bold text-lg px-12 py-4 rounded-full shadow-lg hover:bg-blue-700 transition-all transform hover:scale-105"
                >
                    Planımı Oluştur
                </Link>
            </section>

        </div>
    );
};

export default AboutPage;
